import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { apiEndpoints } from '../config/api';
import './Contact.css';

const enquiryEndpoint = apiEndpoints.propertyEnquiry;

const initialForm = {
  name: '',
  email: '',
  phone: '',
  interest: 'Off-plan launch - Investment - Advisory',
  message: '',
  website: ''
};

const contactMethods = [
  {
    href: 'tel:+97145586224',
    icon: 'fa-solid fa-phone',
    label: 'Office',
    value: '+971 4 558 6224'
  },
  {
    href: 'tel:+971507135432',
    icon: 'fa-solid fa-mobile-screen-button',
    label: 'Mobile',
    value: '+971 50 713 5432'
  },
  {
    href: 'mailto:connect@elevaterealtyuae.com',
    icon: 'fa-regular fa-envelope',
    label: 'Email',
    value: 'connect@elevaterealtyuae.com'
  },
  {
    href: 'https://wa.me/971507135432',
    icon: 'fa-brands fa-whatsapp',
    label: 'WhatsApp',
    value: 'Chat with an advisor'
  }
];

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [formState, setFormState] = useState({ submitting: false, type: '', message: '' });

  useEffect(() => {
    document.body.classList.add('elevate-home', 'inner-page');
    window.scrollTo({ top: 0, behavior: 'auto' });

    const previousTitle = document.title;
    const descriptionElement = document.head.querySelector('meta[name="description"]');
    const previousDescription = descriptionElement?.getAttribute('content') || '';

    document.title = 'Contact - Elevate Realty';
    descriptionElement?.setAttribute(
      'content',
      'Contact Elevate Realty for private Dubai real estate advisory, off-plan investments, villas and penthouses.'
    );

    window.AOS?.init({ duration: 800, once: true, offset: 80 });
    window.AOS?.refreshHard?.();

    return () => {
      document.body.classList.remove('elevate-home', 'inner-page');
      document.title = previousTitle;
      descriptionElement?.setAttribute('content', previousDescription);
    };
  }, []);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setFormState({ submitting: true, type: '', message: '' });

    try {
      const response = await fetch(enquiryEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          source: 'Contact Us Page',
          propertyType: '',
          propertyTitle: 'General website contact',
          referenceNumber: '',
          pageUrl: window.location.href
        })
      });
      const payload = await response.json();
      if (!response.ok || payload.result !== 1) {
        throw new Error(payload.message || 'We could not send your enquiry.');
      }

      setForm(initialForm);
      setFormState({
        submitting: false,
        type: 'success',
        message: 'Thank you. Your enquiry has been sent to our advisory team.'
      });
    } catch (submissionError) {
      setFormState({
        submitting: false,
        type: 'error',
        message: submissionError.message || 'We could not send your enquiry. Please try again.'
      });
    }
  };

  return (
    <>
      <Header />

      <main className="inner-main" id="top">
        <section
          className="inner-hero contact-hero contact-react-hero"
          style={{ '--contact-hero-image': 'url("/assets/img/contact.jpg")' }}
        >
          <div className="inner-hero__content" data-aos="fade-up">
            <p className="eyebrow">Contact</p>
            <h1>Begin a private <em>conversation.</em></h1>
            <p>
              Share your brief with our advisory team. We respond with clarity,
              discretion and next steps tailored to your Dubai plans.
            </p>
          </div>
        </section>

        <section className="inner-section">
          <div className="contact-page-layout">
            <aside className="contact-panel" data-aos="fade-up">
              <p className="eyebrow">Get in touch</p>
              <div className="contact-grid">
                {contactMethods.map((method) => (
                  <a href={method.href} key={method.label}>
                    <i className={method.icon} aria-hidden="true" />
                    <span>{method.label}</span>
                    <strong>{method.value}</strong>
                  </a>
                ))}
              </div>
            </aside>

            <form className="contact-form" onSubmit={submitForm} data-aos="fade-up" data-aos-delay="100">
              <label className="contact-form-honeypot" aria-hidden="true">
                <span>Website</span>
                <input type="text" name="website" value={form.website} tabIndex={-1} autoComplete="off" onChange={updateField} />
              </label>
              <label>
                <span>Full name</span>
                <input required type="text" name="name" value={form.name} placeholder="Your name" autoComplete="name" onChange={updateField} />
              </label>
              <label>
                <span>Email</span>
                <input required type="email" name="email" value={form.email} placeholder="you@example.com" autoComplete="email" onChange={updateField} />
              </label>
              <label>
                <span>Phone</span>
                <input required type="tel" name="phone" value={form.phone} placeholder="+971 ..." autoComplete="tel" onChange={updateField} />
              </label>
              <label>
                <span>Interest</span>
                <select name="interest" value={form.interest} onChange={updateField}>
                  <option>Off-plan launch - Investment - Advisory</option>
                  <option>Private villa</option>
                  <option>Penthouse</option>
                  <option>Investment portfolio</option>
                  <option>Golden Visa advisory</option>
                </select>
              </label>
              <label className="message-field">
                <span>Message</span>
                <textarea required name="message" value={form.message} rows="5" placeholder="Tell us what you’re looking for..." onChange={updateField} />
              </label>
              <button type="submit" disabled={formState.submitting}>
                {formState.submitting ? 'Sending…' : 'Send enquiry'}
              </button>
              {formState.message && (
                <p className={`contact-form-status is-${formState.type}`} role="status">
                  {formState.message}
                </p>
              )}
            </form>
          </div>
        </section>

        <section className="inner-section contact-map-section">
          <iframe
            title="Elevate Realty Dubai location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d925134.3210715873!2d54.568041327437584!3d25.0745656650172!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43496ad9c645%3A0xbde66e5084295162!2sDubai!5e0!3m2!1sen!2sae!4v1784278313568!5m2!1sen!2sae"
            width="100%"
            height="450"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </section>
      </main>

      <Footer />

      <a
        className="advisor-button"
        href="https://api.whatsapp.com/send?phone=971507135432&text=Hi"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Speak with an advisor on WhatsApp"
      >
        <svg className="advisor-button__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21 11.5a8.5 8.5 0 0 1-12.7 7.4L3 20l1.2-5A8.5 8.5 0 1 1 21 11.5Z" />
        </svg>
        <span>Speak with an advisor</span>
      </a>
    </>
  );
};

export default Contact;
